import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { DndProvider, useDrag, useDrop } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { MapContainer, TileLayer, Marker, Popup, Polyline, Tooltip as MapTooltip } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { api, errMsg, fmtNum } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Truck, Loader2, Plus, Save, GripVertical, Trash2, Warehouse, MapPin,
  AlertTriangle, Route as RouteIcon, Building2, Pencil, CalendarClock,
  Wand2, Split,
} from "lucide-react";
import { toast } from "sonner";
import { SupplierPicker } from "@/components/app/SupplierPicker";
import { Switch } from "@/components/ui/switch";

const molkereiIcon = L.divIcon({
  html: `<div style="background:#0f172a;color:#fff;border-radius:8px;padding:3px 7px;font-size:11px;font-weight:600;box-shadow:0 2px 6px rgba(0,0,0,0.35);border:2px solid #fff;white-space:nowrap;">M</div>`,
  className: "", iconSize: [28, 24], iconAnchor: [14, 12],
});

const stopIcon = (position, farbe = "#1d4ed8", hover = false) => {
  const size = hover ? 32 : 26;
  const border = hover ? "3px solid #0f172a" : "2px solid #fff";
  const shadow = hover
    ? "0 4px 14px rgba(0,0,0,0.4)"
    : "0 2px 6px rgba(0,0,0,0.3)";
  return L.divIcon({
    html: `<div style="width:${size}px;height:${size}px;border-radius:9999px;background:${farbe};color:#fff;border:${border};box-shadow:${shadow};display:flex;align-items:center;justify-content:center;font-size:12px;font-weight:700;font-family:ui-sans-serif,system-ui,sans-serif;line-height:1">${position}</div>`,
    className: "",
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
  });
};

// ─── Geometrie- & Planungs-Helper ─────────────────────────────────────────
// Haversine-Distanz (Kilometer)
function haversineKm(a, b) {
  if (a == null || b == null) return Infinity;
  const R = 6371;
  const toRad = (v) => (v * Math.PI) / 180;
  const dLat = toRad(b[0] - a[0]);
  const dLng = toRad(b[1] - a[1]);
  const lat1 = toRad(a[0]);
  const lat2 = toRad(b[0]);
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(h)));
}

// Berechnet Füllstände, Positionen und Warnungen client-seitig (identisch zur Backend-Logik)
function recalcStopsFuellstand(rawStops, kapa) {
  let kum = 0;
  const stops = [];
  const warnungen = [];
  rawStops.forEach((s, idx) => {
    const eintrag = { ...s, position: idx + 1 };
    if (s.typ === "molkerei_return") {
      eintrag.fuellstand_kg = Math.round(kum);
      eintrag.fuellstand_prozent = Math.round((kum / kapa) * 100);
      kum = 0;
    } else {
      const abhol = Number(s.abholmenge_kg || 0);
      kum += abhol;
      eintrag.fuellstand_kg = Math.round(kum);
      eintrag.fuellstand_prozent = Math.round((kum / kapa) * 100);
      if (kum > kapa) {
        warnungen.push({
          position: idx + 1,
          meldung: `Fahrzeug ist voll (Kapazität ${Math.round(kapa)} kg überschritten) – Zwischenentleerung erforderlich.`,
        });
      }
    }
    stops.push(eintrag);
  });
  return { stops, warnungen };
}

// Nearest-Neighbor-Sortierung: startet an der Molkerei, nimmt jeweils den nächstgelegenen Lieferanten.
// Lieferanten ohne Koordinaten werden am Ende (in ursprünglicher Reihenfolge) angehängt.
function sortByProximity(rawStops, molkereiLat, molkereiLng) {
  const suppliers = rawStops.filter((s) => s.typ === "supplier" && s.lat != null && s.lng != null);
  const rest = rawStops.filter((s) => s.typ === "supplier" && (s.lat == null || s.lng == null));
  const returns = rawStops.filter((s) => s.typ === "molkerei_return");
  if (suppliers.length < 2) return rawStops;
  const remaining = [...suppliers];
  const ordered = [];
  let curr = [molkereiLat, molkereiLng];
  while (remaining.length > 0) {
    let bestIdx = 0;
    let bestDist = Infinity;
    for (let i = 0; i < remaining.length; i++) {
      const d = haversineKm(curr, [remaining[i].lat, remaining[i].lng]);
      if (d < bestDist) { bestDist = d; bestIdx = i; }
    }
    const picked = remaining.splice(bestIdx, 1)[0];
    ordered.push(picked);
    curr = [picked.lat, picked.lng];
  }
  // Bestehende Rückfahrten bleiben erhalten (werden separat "auto-verteilt" wenn gewünscht),
  // hier aber platzieren wir sie einfach am Ende, damit sie nicht die Reihenfolge stören.
  return [...ordered, ...rest, ...returns];
}

// Rückfahrten automatisch dort einfügen, wo die Kapazität überschritten würde.
// Entfernt zuvor alle bestehenden Rückfahrten und setzt neue nach Bedarf.
function autoInsertReturns(rawStops, kapa) {
  const withoutReturns = rawStops.filter((s) => s.typ !== "molkerei_return");
  const result = [];
  let kum = 0;
  let idxCounter = 0;
  withoutReturns.forEach((s) => {
    const abhol = Number(s.abholmenge_kg || 0);
    if (kum + abhol > kapa && result.length > 0 && result[result.length - 1].typ !== "molkerei_return") {
      // Vor diesem Stop eine Rückfahrt einfügen
      result.push({
        typ: "molkerei_return",
        id: `auto-${Date.now()}-${idxCounter++}`,
      });
      kum = 0;
    }
    result.push(s);
    kum += abhol;
  });
  return result;
}

const StopItemDnd = ({ stop, index, moveStop, onHover, onLeave, onRemove, hover, kapa, segmentFarbe }) => {
  const ref = useRef(null);
  const [, drop] = useDrop({
    accept: "STOP",
    hover(item) {
      if (item.index === index) return;
      moveStop(item.index, index);
      item.index = index;
    },
  });
  const [{ isDragging }, drag] = useDrag({
    type: "STOP", item: { index },
    collect: (m) => ({ isDragging: m.isDragging() }),
  });
  drag(drop(ref));

  const voll = stop.fuellstand_kg > kapa;
  const pct = Math.min(100, stop.fuellstand_prozent || 0);
  const active = hover === stop.liefernummer;
  const isReturn = stop.typ === "molkerei_return";

  // Basis-Klassen abhängig vom Stop-Typ – Rückfahrten deutlich abgehoben.
  const baseClasses = isReturn
    ? "bg-amber-50 dark:bg-amber-950/25 border-amber-300 dark:border-amber-800"
    : "bg-card";

  return (
    <div ref={ref}
      onMouseEnter={() => !isReturn && onHover(stop.liefernummer)}
      onMouseLeave={onLeave}
      className={`rounded-lg border p-2.5 transition-all ${baseClasses} ${
        isDragging ? "opacity-40" : ""} ${active ? "ring-2 ring-primary" : ""} ${
        voll && !isReturn ? "border-destructive" : ""}`}
      data-testid={`tour-stop-${index}`}
    >
      <div className="flex items-center gap-2">
        <GripVertical className={`h-4 w-4 cursor-grab shrink-0 ${isReturn ? "text-amber-700 dark:text-amber-500" : "text-muted-foreground"}`} />
        <div className={`flex h-8 w-8 items-center justify-center rounded-md text-xs font-semibold shrink-0 ${
          isReturn
            ? "bg-amber-500/20 text-amber-800 dark:text-amber-300"
            : "bg-primary/10 text-primary"
        }`}>
          {isReturn ? <Warehouse className="h-4 w-4" /> : index + 1}
        </div>
        {isReturn ? (
          <>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-amber-900 dark:text-amber-200 flex items-center gap-1.5">
                Rückfahrt zur Molkerei
                {segmentFarbe && (
                  <span
                    className="inline-block h-2 w-2 rounded-full"
                    style={{ background: segmentFarbe }}
                    title="Ende dieser Runde"
                  />
                )}
              </p>
              <p className="text-xs text-amber-700/80 dark:text-amber-300/70 truncate">
                Entleerung · Füllstand vor Rückfahrt: <span className="font-num">{fmtNum(stop.fuellstand_kg || 0)} kg</span>
              </p>
            </div>
          </>
        ) : (
          <>
            <MapPin className="h-4 w-4 text-primary shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate">{stop.bezeichnung}</p>
              <p className="text-[11px] text-muted-foreground truncate flex items-center gap-1 flex-wrap">
                <span>{[stop.hofname, stop.ort].filter(Boolean).join(" · ")}</span>
                {stop.abholmenge_kg ? (
                  <>
                    <span>·</span>
                    <span className="font-num" data-testid={`stop-abholmenge-${stop.liefernummer}`}>
                      Ø {fmtNum(stop.abholmenge_kg)} kg
                    </span>
                    {stop.zyklus_tage > 1 && (
                      <span className="text-muted-foreground">
                        ({stop.zyklus_tage}-Tages-Zyklus · {fmtNum(stop.tagesmenge_kg)} kg/Tag)
                      </span>
                    )}
                    {stop.tagesmenge_basis === "geschaetzt" && (
                      <span
                        className="text-[10px] uppercase tracking-wide bg-secondary rounded px-1 py-0.5"
                        title="Geschätzte Tagesmenge aus den Stammdaten – noch keine ausreichenden Lieferdaten vorhanden."
                      >
                        Schätzung
                      </span>
                    )}
                    {stop.tagesmenge_basis === "unbekannt" && (
                      <span
                        className="text-[10px] uppercase tracking-wide bg-destructive/10 text-destructive rounded px-1 py-0.5"
                        title="Weder Stammdaten-Schätzung noch historische Daten vorhanden. Bitte Tagesmenge in Lieferantenstammdaten pflegen."
                      >
                        keine Menge
                      </span>
                    )}
                    {(stop.lat == null || stop.lng == null) && (
                      <span
                        className="text-[10px] uppercase tracking-wide bg-amber-500/15 text-amber-700 dark:text-amber-400 rounded px-1 py-0.5"
                        title="Kein Standort hinterlegt – dieser Lieferant erscheint nicht als Marker auf der Karte. Bitte Adresse/Koordinaten in den Stammdaten ergänzen."
                        data-testid={`stop-no-geo-${stop.liefernummer}`}
                      >
                        nicht auf Karte
                      </span>
                    )}
                  </>
                ) : (
                  <>
                    <span>·</span>
                    <span
                      className="text-[10px] uppercase tracking-wide bg-destructive/10 text-destructive rounded px-1 py-0.5"
                      title="Keine Tagesmenge hinterlegt. Bitte in Lieferantenstammdaten Schätzwert eintragen."
                    >
                      keine Menge
                    </span>
                  </>
                )}
              </p>
            </div>
            {/* Desktop: Füllstand rechts inline */}
            <div className="hidden sm:block w-24 text-right shrink-0">
              <p className={`text-xs font-num font-medium ${voll ? "text-destructive" : ""}`}>
                {fmtNum(stop.fuellstand_kg)} kg
              </p>
              <Progress value={pct} className={`h-1.5 mt-0.5 ${voll ? "[&>*]:bg-destructive" : ""}`} />
              <p className="text-[10px] text-muted-foreground">{pct}%</p>
            </div>
          </>
        )}
        <Button variant="ghost" size="icon" onClick={() => onRemove(index)} className="shrink-0 h-8 w-8" title="Entfernen">
          <Trash2 className="h-3.5 w-3.5 text-destructive" />
        </Button>
      </div>
      {/* Mobile: Füllstand-Balken unter dem Header (volle Breite) */}
      {stop.typ === "supplier" && (
        <div className="sm:hidden mt-2 flex items-center gap-2">
          <Progress value={pct} className={`h-1.5 flex-1 ${voll ? "[&>*]:bg-destructive" : ""}`} />
          <span className={`text-[11px] font-num font-medium tabular-nums ${voll ? "text-destructive" : "text-muted-foreground"}`}>
            {fmtNum(stop.fuellstand_kg)} kg · {pct}%
          </span>
        </div>
      )}
    </div>
  );
};

const TourDialog = ({ open, onClose, onSaved, tour }) => {
  const isEdit = Boolean(tour?.id);
  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({
    code: "", name: "", fahrzeug_kapazitaet_kg: 20000, farbe: "#1d4ed8",
    molkerei_lat: 48.0035, molkerei_lng: 13.4830, aktiv: true,
    haeufigkeit_tage: 2, startdatum: today,
  });
  const [saving, setSaving] = useState(false);

  // Beim Öffnen: Formular mit den Daten der Tour befüllen (oder Defaults)
  useEffect(() => {
    if (open) {
      setForm(tour ? {
        code: tour.code || "",
        name: tour.name || "",
        fahrzeug_kapazitaet_kg: tour.fahrzeug_kapazitaet_kg ?? 20000,
        farbe: tour.farbe || "#1d4ed8",
        molkerei_lat: tour.molkerei_lat ?? 48.0035,
        molkerei_lng: tour.molkerei_lng ?? 13.4830,
        aktiv: tour.aktiv ?? true,
        haeufigkeit_tage: tour.haeufigkeit_tage ?? 2,
        startdatum: tour.startdatum || today,
      } : {
        code: "", name: "", fahrzeug_kapazitaet_kg: 20000, farbe: "#1d4ed8",
        molkerei_lat: 48.0035, molkerei_lng: 13.4830, aktiv: true,
        haeufigkeit_tage: 2, startdatum: today,
      });
    }
  }, [open, tour, today]);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        ...form,
        fahrzeug_kapazitaet_kg: Number(form.fahrzeug_kapazitaet_kg),
        molkerei_lat: Number(form.molkerei_lat),
        molkerei_lng: Number(form.molkerei_lng),
        haeufigkeit_tage: Math.max(1, Number(form.haeufigkeit_tage) || 1),
        startdatum: form.startdatum || null,
      };
      if (isEdit) {
        await api.put(`/touren/${tour.id}`, payload);
        toast.success("Tour aktualisiert.");
      } else {
        await api.post("/touren", payload);
        toast.success("Tour angelegt.");
      }
      onSaved();
      onClose();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setSaving(false); }
  };

  // Preview der nächsten Fahrtage lokal berechnen
  const preview = React.useMemo(() => {
    const step = Math.max(1, Number(form.haeufigkeit_tage) || 1);
    const startStr = form.startdatum || today;
    const start = new Date(startStr + "T00:00:00");
    const now = new Date(today + "T00:00:00");
    let cur = start > now ? new Date(start) : new Date(now);
    const diffDays = Math.floor((cur - start) / 86400000);
    const rest = ((diffDays % step) + step) % step;
    if (rest !== 0) cur.setDate(cur.getDate() + (step - rest));
    const out = [];
    for (let i = 0; i < 4; i++) {
      out.push(new Date(cur.getTime() + i * step * 86400000)
        .toLocaleDateString("de-AT", { weekday: "short", day: "2-digit", month: "2-digit" }));
    }
    return out;
  }, [form.haeufigkeit_tage, form.startdatum, today]);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="tour-dialog" className="max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {isEdit ? `Tour bearbeiten – ${tour.code}` : "Neue Tour anlegen"}
          </DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label>Code *</Label>
            <Input value={form.code}
              onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
              data-testid="tour-form-code" placeholder="T04"
              disabled={isEdit}
            />
            {isEdit && (
              <p className="text-[11px] text-muted-foreground">
                Tour-Code kann nach Anlage nicht geändert werden.
              </p>
            )}
          </div>
          <div className="space-y-1.5">
            <Label>Name *</Label>
            <Input value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              data-testid="tour-form-name" placeholder="Sammeltour Ost" />
          </div>
          <div className="space-y-1.5 col-span-2">
            <Label>Fahrzeug-Kapazität (kg)</Label>
            <Input type="number" step="500" min="0"
              value={form.fahrzeug_kapazitaet_kg}
              data-testid="tour-form-kapa"
              onChange={(e) => setForm({ ...form, fahrzeug_kapazitaet_kg: e.target.value })} />
            <p className="text-[11px] text-muted-foreground">
              Maximale Füllmenge des Sammelwagens dieser Tour. Wird für Füllstands-Anzeige
              und Kapazitäts-Warnungen bei der Routenplanung verwendet.
            </p>
          </div>

          <div className="space-y-1.5">
            <Label>Fahrrhythmus (alle X Tage) *</Label>
            <div className="flex gap-2">
              <Select value={String(form.haeufigkeit_tage)}
                onValueChange={(v) => setForm({ ...form, haeufigkeit_tage: Number(v) })}>
                <SelectTrigger data-testid="tour-form-haeufigkeit"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">täglich (1 Tag)</SelectItem>
                  <SelectItem value="2">jeden 2. Tag</SelectItem>
                  <SelectItem value="3">jeden 3. Tag</SelectItem>
                  <SelectItem value="4">jeden 4. Tag</SelectItem>
                  <SelectItem value="7">wöchentlich (7 Tage)</SelectItem>
                  <SelectItem value="14">14-tägig</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>Startdatum *</Label>
            <Input type="date" value={form.startdatum}
              data-testid="tour-form-startdatum"
              onChange={(e) => setForm({ ...form, startdatum: e.target.value })} />
            <p className="text-[11px] text-muted-foreground">
              Ab wann der Rhythmus zählt. Kann rückwirkend sein.
            </p>
          </div>
          <div className="col-span-2 rounded-md border bg-secondary/50 p-2 text-xs" data-testid="tour-form-preview">
            <p className="text-muted-foreground mb-1">Nächste Fahrtage (Vorschau):</p>
            <p className="font-medium">{preview.join("  ·  ")}</p>
          </div>

          <div className="space-y-1.5">
            <Label>Linienfarbe</Label>
            <Input type="color" value={form.farbe}
              onChange={(e) => setForm({ ...form, farbe: e.target.value })} />
          </div>
          <div className="space-y-1.5">
            <Label>Status</Label>
            <div className="flex items-center gap-2 h-10">
              <Switch checked={form.aktiv} data-testid="tour-form-aktiv"
                onCheckedChange={(v) => setForm({ ...form, aktiv: v })} />
              <span className="text-sm">{form.aktiv ? "Aktiv" : "Inaktiv"}</span>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Abbrechen</Button>
          <Button onClick={save} disabled={saving || !form.code || !form.name}
            data-testid="tour-dialog-save">
            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
            {isEdit ? "Speichern" : "Anlegen"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default function AdminTourenPage() {
  const [touren, setTouren] = useState([]);
  const [aktivId, setAktivId] = useState(null);
  const [plan, setPlan] = useState(null);
  const [lieferanten, setLieferanten] = useState([]);
  const [hover, setHover] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [neueTour, setNeueTour] = useState(false);
  const [tourEdit, setTourEdit] = useState(null); // { ...tour } wenn Bearbeiten-Dialog offen
  const [dirty, setDirty] = useState(false);

  const loadTouren = useCallback(() => {
    return Promise.all([api.get("/touren"), api.get("/lieferanten")])
      .then(([t, l]) => {
        setTouren(t.data); setLieferanten(l.data);
        if (t.data.length && !aktivId) setAktivId(t.data[0].id);
      });
  }, [aktivId]);

  useEffect(() => {
    loadTouren().finally(() => setLoading(false));
  }, [loadTouren]);

  const loadPlan = useCallback(() => {
    if (!aktivId) return;
    api.get(`/touren/${aktivId}/planung`).then((res) => { setPlan(res.data); setDirty(false); });
  }, [aktivId]);

  useEffect(() => { loadPlan(); }, [loadPlan]);

  const tour = plan?.tour;
  const stops = useMemo(() => plan?.stops || [], [plan?.stops]);
  const kapa = tour?.fahrzeug_kapazitaet_kg || 20000;

  // Berechnet Füllstände + Warnungen live neu und aktualisiert den Plan-State.
  // Wird nach jedem Reorder/Add/Remove/Quick-Action aufgerufen.
  const applyStopUpdate = useCallback((newStops) => {
    if (!plan) return;
    const { stops: recalc, warnungen } = recalcStopsFuellstand(newStops, kapa);
    setPlan((p) => ({ ...p, stops: recalc, warnungen }));
    setDirty(true);
  }, [plan, kapa]);

  const moveStop = useCallback((from, to) => {
    setPlan((p) => {
      if (!p) return p;
      const s = [...p.stops];
      const [item] = s.splice(from, 1);
      s.splice(to, 0, item);
      const { stops: recalc, warnungen } = recalcStopsFuellstand(s, kapa);
      return { ...p, stops: recalc, warnungen };
    });
    setDirty(true);
  }, [kapa]);

  const removeStop = useCallback((idx) => {
    setPlan((p) => {
      if (!p) return p;
      const filtered = p.stops.filter((_, i) => i !== idx);
      const { stops: recalc, warnungen } = recalcStopsFuellstand(filtered, kapa);
      return { ...p, stops: recalc, warnungen };
    });
    setDirty(true);
  }, [kapa]);

  const addMolkereiReturn = () => {
    if (!plan) return;
    applyStopUpdate([
      ...plan.stops,
      { typ: "molkerei_return", id: `tmp-${Date.now()}`, abholmenge_kg: 0 },
    ]);
  };

  const addSupplier = (liefernummer) => {
    if (!plan) return;
    const s = lieferanten.find((x) => x.liefernummer === liefernummer);
    if (!s) return;
    // Erwartete Abholmenge = tages_menge * zyklus_tage (siehe Backend-Logik)
    const zyklus = plan.tour?.haeufigkeit_tage || 1;
    const tagesMenge = s.effektive_tagesmenge_kg || 0;
    const abhol = Math.round(tagesMenge * zyklus);
    applyStopUpdate([
      ...plan.stops,
      {
        typ: "supplier", liefernummer,
        bezeichnung: `${s.liefernummer} · ${s.name}`, hofname: s.hofname, ort: s.ort,
        lat: s.lat, lng: s.lng, milchsorte_id: s.milchsorte_id,
        tagesmenge_kg: Math.round(tagesMenge),
        tagesmenge_basis: s.tagesmenge_basis || "unbekannt",
        abholmenge_kg: abhol,
        zyklus_tage: zyklus,
        menge_tag_kg: abhol,
        menge_monat_kg: Math.round(tagesMenge * 30),
        id: `tmp-${Date.now()}`,
      },
    ]);
  };

  // Quick-Action: Nearest-Neighbor-Sortierung nach Nähe zur Molkerei.
  const quickSortByProximity = () => {
    if (!plan || !tour) return;
    const sorted = sortByProximity(plan.stops, tour.molkerei_lat, tour.molkerei_lng);
    applyStopUpdate(sorted);
    toast.success("Reihenfolge nach Nähe optimiert. Bitte prüfen und speichern.");
  };

  // Quick-Action: Rückfahrten automatisch dort einfügen, wo Kapazität überschritten würde.
  const quickAutoReturns = () => {
    if (!plan || !tour) return;
    const withReturns = autoInsertReturns(plan.stops, kapa);
    applyStopUpdate(withReturns);
    const anzahlReturns = withReturns.filter((s) => s.typ === "molkerei_return").length;
    toast.success(
      anzahlReturns === 0
        ? "Kapazität reicht ohne Rückfahrten. Keine Änderung nötig."
        : `${anzahlReturns} Rückfahrt${anzahlReturns > 1 ? "en" : ""} automatisch eingefügt.`,
    );
  };

  const speichern = async () => {
    setSaving(true);
    try {
      const payload = { stops: stops.map((s) => ({
        typ: s.typ, liefernummer: s.liefernummer || null, bemerkung: s.bemerkung || "",
      })) };
      await api.put(`/touren/${aktivId}/planung`, payload);
      toast.success("Tour gespeichert.");
      loadPlan();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setSaving(false); }
  };

  // Route in Segmente splitten: Nach jeder Rückfahrt zur Molkerei beginnt eine neue "Runde".
  // Jede Runde bekommt eine eigene Farbe, um die Trip-Segmente visuell zu unterscheiden.
  const segments = useMemo(() => {
    if (!tour) return [];
    const result = [];
    let current = [[tour.molkerei_lat, tour.molkerei_lng]];
    for (const s of stops) {
      if (s.typ === "molkerei_return") {
        current.push([tour.molkerei_lat, tour.molkerei_lng]);
        if (current.length > 1) result.push(current);
        // Nächstes Segment startet wieder an der Molkerei
        current = [[tour.molkerei_lat, tour.molkerei_lng]];
      } else if (s.lat != null && s.lng != null) {
        current.push([s.lat, s.lng]);
      }
    }
    // Letztes Segment mit Rückweg zur Molkerei abschließen (nur wenn Zwischenstopps existieren)
    if (current.length > 1) {
      current.push([tour.molkerei_lat, tour.molkerei_lng]);
      result.push(current);
    }
    return result;
  }, [stops, tour]);

  // Farbpalette für Segmente – erste Runde nutzt die Tour-Grundfarbe, danach klar abgegrenzte Kontrastfarben
  const segmentFarbe = useCallback((idx) => {
    const palette = [
      tour?.farbe || "#1d4ed8",
      "#8b5cf6", // Violett
      "#f59e0b", // Amber
      "#10b981", // Emerald
      "#ef4444", // Rot
      "#0ea5e9", // Sky
    ];
    return palette[idx % palette.length];
  }, [tour?.farbe]);

  const bounds = useMemo(() => {
    if (!tour) return [[47.8, 13.25], [48.17, 13.7]];
    const pts = [[tour.molkerei_lat, tour.molkerei_lng]];
    for (const s of stops) if (s.lat != null && s.lng != null) pts.push([s.lat, s.lng]);
    return pts.length > 1 ? pts : [[47.8, 13.25], [48.17, 13.7]];
  }, [stops, tour]);

  const verfuegbareLieferanten = lieferanten
    .filter((l) => !stops.some((s) => s.liefernummer === l.liefernummer))
    .filter((l) => l.lat != null && l.lng != null);

  if (loading)
    return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;

  return (
    <DndProvider backend={HTML5Backend}>
      <div className="space-y-5">
        <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-center sm:justify-between gap-3">
          <div className="min-w-0">
            <h1 className="text-2xl font-semibold">Tourenplanung</h1>
            <p className="text-sm text-muted-foreground">
              Reihenfolge per Drag-and-Drop · Live-Füllstand basierend auf Ø-Liefermenge · Route auf Karte
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Select value={aktivId || ""} onValueChange={setAktivId}>
              <SelectTrigger className="w-full sm:w-[220px]" data-testid="tour-select">
                <SelectValue placeholder="Tour wählen" />
              </SelectTrigger>
              <SelectContent>
                {touren.map((t) => (
                  <SelectItem key={t.id} value={t.id}>
                    <span className="inline-block h-2 w-2 rounded-full mr-2" style={{ background: t.farbe }} />
                    {t.code} · {t.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={() => setNeueTour(true)} data-testid="tour-new-button">
              <Plus className="h-4 w-4 mr-1" /> Neue Tour
            </Button>
          </div>
        </div>

        {tour && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            <Card>
              <CardHeader className="pb-2 flex flex-col md:flex-row md:items-start md:justify-between gap-3">
                <div className="min-w-0">
                  <CardTitle className="text-base flex items-center gap-2">
                    <RouteIcon className="h-4 w-4 text-primary shrink-0" />
                    <span className="truncate">{tour.code} · {tour.name}</span>
                  </CardTitle>
                  <CardDescription>
                    Kapazität {fmtNum(kapa)} kg · {stops.length} Stopps
                    {plan.warnungen?.length > 0 && (
                      <span className="ml-2 inline-flex items-center gap-1 text-destructive">
                        <AlertTriangle className="h-3 w-3" /> {plan.warnungen.length} Warnung(en)
                      </span>
                    )}
                  </CardDescription>
                  {tour.naechste_fahrtage?.length > 0 && (
                    <div className="mt-2 rounded-md border bg-secondary/50 p-2" data-testid="tour-naechste-fahrtage">
                      <p className="text-[11px] text-muted-foreground">
                        <CalendarClock className="h-3 w-3 inline mr-1" />
                        Nächste Fahrtage · Rhythmus alle {tour.haeufigkeit_tage || 1} Tag(e)
                      </p>
                      <div className="mt-1 flex flex-wrap gap-1">
                        {tour.naechste_fahrtage.slice(0, 6).map((d) => (
                          <Badge key={d} variant="outline" className="text-[11px] font-num">
                            {new Date(d + "T00:00:00").toLocaleDateString("de-AT", {
                              weekday: "short", day: "2-digit", month: "2-digit",
                            })}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                <div className="flex gap-1.5 flex-wrap justify-end sticky top-0 z-10 bg-card pb-1">
                  <Button variant="ghost" size="sm" onClick={() => setTourEdit(tour)}
                    data-testid="tour-edit-button" title="Tour bearbeiten (Kapazität, Name, Farbe)">
                    <Pencil className="h-4 w-4 mr-1" /> Bearbeiten
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={quickSortByProximity}
                    disabled={stops.filter((s) => s.typ === "supplier" && s.lat != null).length < 2}
                    data-testid="tour-quick-sort"
                    title="Reihenfolge nach Nähe zur Molkerei sortieren (Nearest-Neighbor-Heuristik)"
                  >
                    <Wand2 className="h-4 w-4 mr-1" /> Nach Nähe
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={quickAutoReturns}
                    disabled={stops.filter((s) => s.typ === "supplier").length === 0}
                    data-testid="tour-quick-returns"
                    title="Rückfahrten automatisch dort einfügen, wo die LKW-Kapazität überschritten würde"
                  >
                    <Split className="h-4 w-4 mr-1" /> Rückfahrten auto
                  </Button>
                  <Button variant="outline" size="sm" onClick={addMolkereiReturn} data-testid="tour-add-return">
                    <Building2 className="h-4 w-4 mr-1" /> Rückfahrt
                  </Button>
                  <Button size="sm" onClick={speichern} disabled={saving || !dirty} data-testid="tour-save-button">
                    {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                    Speichern
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 max-h-[420px] sm:max-h-[540px] xl:max-h-[calc(100vh-22rem)] xl:min-h-[540px] overflow-y-auto pr-1" data-testid="tour-stops-list">
                  {(() => {
                    let returnIdx = 0;
                    return stops.map((s, i) => {
                      const currentReturnIdx = s.typ === "molkerei_return" ? returnIdx : null;
                      if (s.typ === "molkerei_return") returnIdx += 1;
                      return (
                        <StopItemDnd
                          key={s.id || `${s.typ}-${i}`}
                          stop={s}
                          index={i}
                          moveStop={moveStop}
                          onHover={setHover}
                          onLeave={() => setHover(null)}
                          onRemove={removeStop}
                          hover={hover}
                          kapa={kapa}
                          segmentFarbe={currentReturnIdx != null ? segmentFarbe(currentReturnIdx) : null}
                        />
                      );
                    });
                  })()}
                </div>
                <div className="mt-3 border-t pt-3">
                  <Label className="text-xs">Lieferant hinzufügen</Label>
                  <SupplierPicker value=""
                    options={verfuegbareLieferanten}
                    testId="tour-add-supplier-picker"
                    placeholder="Lieferant suchen und hinzufügen …"
                    allowClear={false}
                    onChange={(v) => v && addSupplier(v)}
                    className="mt-1" />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <Truck className="h-4 w-4 text-primary" /> Tourenkarte
                </CardTitle>
                <CardDescription>Start und Ende: Molkerei · Zwischenstopps in Reihenfolge</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-[320px] sm:h-[420px] lg:h-[540px] xl:h-[calc(100vh-22rem)] xl:min-h-[540px] rounded-lg overflow-hidden z-0" data-testid="tour-map">
                  <MapContainer bounds={bounds} boundsOptions={{ padding: [30, 30] }}
                    scrollWheelZoom style={{ height: "100%", width: "100%" }}>
                    <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                      attribution='&copy; OpenStreetMap' />
                    {segments.map((seg, idx) => (
                      <Polyline
                        key={`segment-${idx}`}
                        positions={seg}
                        pathOptions={{ color: segmentFarbe(idx), weight: 3, dashArray: "6 5" }}
                      />
                    ))}
                    <Marker
                      position={[tour.molkerei_lat, tour.molkerei_lng]}
                      icon={molkereiIcon}
                      zIndexOffset={2000}
                    >
                      <MapTooltip permanent direction="top" offset={[0, -12]} opacity={0.9}>Molkerei</MapTooltip>
                    </Marker>
                    {(() => {
                      // Marker-Nummer ist konsistent mit der Listen-Position (i+1),
                      // damit Hover-Mapping zwischen Liste und Karte eindeutig ist.
                      return stops.map((s, i) => {
                        if (s.typ !== "supplier" || s.lat == null || s.lng == null) return null;
                        const pos = i + 1;
                        const isHover = hover === s.liefernummer;
                        return (
                          <Marker
                            // key inkludiert Hover-Zustand, damit react-leaflet den Marker sauber
                            // neu mountet und das aktualisierte Icon (Größe/Rand) übernimmt.
                            key={`${s.id || `${s.typ}-${i}`}-${isHover ? "h" : "n"}`}
                            position={[s.lat, s.lng]}
                            icon={stopIcon(pos, tour.farbe || "#1d4ed8", isHover)}
                            // zIndexOffset sorgt dafür, dass die Marker stets über den TileLayers
                            // liegen; gehoverte Marker werden zusätzlich nach oben priorisiert.
                            zIndexOffset={isHover ? 1500 : 1000}
                            eventHandlers={{
                              mouseover: () => setHover(s.liefernummer),
                              mouseout: () => setHover(null),
                            }}
                          >
                            <MapTooltip>Pos. {pos}: {s.bezeichnung}</MapTooltip>
                          </Marker>
                        );
                      });
                    })()}
                  </MapContainer>
                </div>
                {/* Legende für Segmente – nur anzeigen, wenn Rückfahrten die Route splitten */}
                {segments.length > 1 && (
                  <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-muted-foreground" data-testid="tour-segment-legend">
                    <span className="font-medium text-foreground">Runden:</span>
                    {segments.map((_, idx) => (
                      <span key={idx} className="inline-flex items-center gap-1.5">
                        <span
                          className="inline-block h-0.5 w-6 rounded"
                          style={{ background: segmentFarbe(idx), borderTop: `2px dashed ${segmentFarbe(idx)}` }}
                        />
                        Runde {idx + 1}
                      </span>
                    ))}
                  </div>
                )}
                {plan.warnungen?.length > 0 && (
                  <div className="mt-3 rounded-lg border border-destructive/40 bg-destructive/5 p-3 space-y-1">
                    {plan.warnungen.map((w, i) => (
                      <div key={i} className="text-xs text-destructive flex items-start gap-1.5">
                        <AlertTriangle className="h-3.5 w-3.5 mt-0.5" />
                        <span>Position {w.position}: {w.meldung}</span>
                      </div>
                    ))}
                    <p className="text-[11px] text-muted-foreground pt-1 border-t border-destructive/20">
                      Fügen Sie eine „Rückfahrt zur Molkerei“ ein, um das Fahrzeug zu entleeren.
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        <TourDialog
          open={neueTour}
          tour={null}
          onClose={() => setNeueTour(false)}
          onSaved={() => { loadTouren(); }}
        />
        <TourDialog
          open={Boolean(tourEdit)}
          tour={tourEdit}
          onClose={() => setTourEdit(null)}
          onSaved={() => { loadTouren(); loadPlan(); }}
        />
      </div>
    </DndProvider>
  );
}
